// src/pages/Projects.js
import React from 'react';
import { Box, Typography, Grid, Card, CardActionArea, CardContent, Chip } from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import products from '../data/products';
import { pinkGradient, pastels } from '../colors';

const accentCycle = [pastels.pink, pastels.lavender, pastels.sky, pastels.mint, pastels.peach];

// Flip `live: true` and add a `link` once an app ships.
const appChallenge = [
  { title: 'You Qualify', tagline: 'college aid you already qualify for', emoji: '🎓', live: false, link: null },
  { title: 'Tranquilo', tagline: 'a calm plan for immigration fear', emoji: '🕊️', live: false, link: null },
  { title: 'Still Happening', tagline: "what's still happening, sourced", emoji: '📰', live: false, link: null },
  { title: 'Loose Ends', tagline: 'find your forgotten accounts', emoji: '🧹', live: false, link: null },
  { title: 'Last Seen', tagline: 'where diaspora food was last seen', emoji: '🍲', live: false, link: null },
  { title: 'Turns', tagline: 'household labor, made visible', emoji: '🔄', live: false, link: null },
  { title: 'Two Kitchens', tagline: 'cook with a stranger, live', emoji: '🍳', live: false, link: null },
  { title: 'Wearwithal', tagline: 'pack from what you already own', emoji: '🎒', live: false, link: null },
  { title: 'Again', tagline: 'for the session you missed', emoji: '💪', live: false, link: null },
  { title: 'Before You Pay', tagline: 'the truth before you pay a scammer', emoji: '🚫', live: false, link: null },
  { title: 'Milliamp', tagline: 'power budgets for off-grid gear', emoji: '🔋', live: false, link: null },
  { title: 'Turned Out', tagline: "a stranger's junk drawer, shared", emoji: '🗄️', live: false, link: null },
  { title: 'Reflection', tagline: 'daily reflection against a shared text', emoji: '🕯️', live: false, link: null },
  { title: 'Calendar Kitchen', tagline: 'dinners planned around your week', emoji: '📅', live: false, link: null },
  { title: 'Shopping List & Budget', tagline: 'a shopping list with a budget built in', emoji: '🛍️', live: false, link: null }
].map((app, i) => ({ ...app, accent: accentCycle[i % accentCycle.length] }));

const TOTAL_WEEKS = appChallenge.length;
const CHALLENGE_START = new Date('2026-09-17T00:00:00');
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function getCurrentWeek() {
  const elapsed = Date.now() - CHALLENGE_START.getTime();
  if (elapsed < 0) return 0;
  return Math.min(TOTAL_WEEKS, Math.floor(elapsed / WEEK_MS) + 1);
}

function IconBubble({ emoji, color, size = 44 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        mx: 'auto',
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.5,
        background: `${color}55`
      }}
    >
      {emoji}
    </Box>
  );
}

export default function Projects() {
  const currentWeek = getCurrentWeek();
  const shippedCount = appChallenge.filter((a) => a.live).length;
  const progressPct = (shippedCount / TOTAL_WEEKS) * 100;

  return (
    <>
      <Typography variant="h4" component="h1" sx={{ mb: 4, textAlign: 'center' }}>
        Projects 🌸
      </Typography>

      <Box sx={{ mb: 5 }}>
        <Typography variant="h6" gutterBottom>
          ✨ Shipped
        </Typography>
        <Grid container spacing={2}>
          {products.map((p, i) => (
            <Grid item xs={6} key={p.title}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 }
                }}
              >
                <CardActionArea
                  href={p.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ height: '100%' }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <IconBubble emoji={p.icon} color={accentCycle[i % accentCycle.length]} />
                    <Typography variant="subtitle2">{p.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="h6">🚀 15 Apps in 15 Weeks</Typography>
          {currentWeek > 0 && (
            <Typography variant="caption" color="text.secondary">
              Week {currentWeek} of {TOTAL_WEEKS}
            </Typography>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {shippedCount === 0
            ? "Building and shipping 15 small apps in 15 weeks. First one's in progress!"
            : `${shippedCount} of ${TOTAL_WEEKS} shipped. Building and shipping the rest as the weeks go.`}
        </Typography>

        {/* Progress bar */}
        <Box
          sx={{
            position: 'relative',
            height: 12,
            borderRadius: 6,
            bgcolor: 'action.hover',
            overflow: 'visible',
            mb: 3
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              width: `${Math.max(progressPct, 3)}%`,
              minWidth: 12,
              borderRadius: 6,
              overflow: 'hidden',
              background: pinkGradient,
              backgroundSize: '200% 100%',
              transition: 'width 0.8s ease',
              animation: 'shimmer 3s linear infinite',
              '@keyframes shimmer': {
                '0%': { backgroundPosition: '0% 50%' },
                '100%': { backgroundPosition: '200% 50%' }
              }
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: `${Math.max(progressPct, 3)}%`,
              fontSize: 16,
              transform: 'translate(-50%, -50%)',
              animation: 'bob 1.6s ease-in-out infinite',
              '@keyframes bob': {
                '0%, 100%': { transform: 'translate(-50%, -55%)' },
                '50%': { transform: 'translate(-50%, -45%)' }
              }
            }}
          >
            ⭐
          </Box>
        </Box>

        <Grid container spacing={1.5}>
          {appChallenge.map((app, i) => {
            const week = i + 1;
            const isCurrent = week === currentWeek && !app.live;

            const hasBadge = app.live || isCurrent;
            const cardBody = (
              <Box sx={{ pt: hasBadge ? 3.5 : 2, pb: 2, px: 1 }}>
                <IconBubble emoji={app.emoji} color={app.accent} size={36} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Week {week}
                </Typography>
                <Typography variant="subtitle2">{app.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {app.tagline}
                </Typography>
              </Box>
            );

            return (
              <Grid item xs={6} sm={4} key={app.title}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    position: 'relative',
                    opacity: app.live ? 1 : isCurrent ? 1 : 0.85,
                    borderTop: `3px solid ${app.accent}`,
                    borderColor: isCurrent ? 'primary.main' : app.live ? 'success.main' : undefined,
                    borderWidth: isCurrent || app.live ? 2 : 1,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease, opacity 0.3s ease',
                    animation: 'fadeInUp 0.5s ease backwards',
                    animationDelay: `${i * 0.04}s`,
                    '@keyframes fadeInUp': {
                      '0%': { opacity: 0, transform: 'translateY(8px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' }
                    },
                    ...(isCurrent && {
                      animation: 'fadeInUp 0.5s ease backwards, glow 2s ease-in-out infinite',
                      animationDelay: `${i * 0.04}s, 0s`,
                      '@keyframes glow': {
                        '0%, 100%': { boxShadow: '0 0 0 0 rgba(199,125,186,0.35)' },
                        '50%': { boxShadow: '0 0 0 6px rgba(199,125,186,0)' }
                      }
                    }),
                    '&:hover': { transform: 'translateY(-3px) scale(1.02)', boxShadow: 4 }
                  }}
                >
                  {app.live && (
                    <Chip
                      icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                      label="Live"
                      color="success"
                      size="small"
                      sx={{ position: 'absolute', top: 6, right: 6, height: 20, fontSize: 10, zIndex: 1 }}
                    />
                  )}
                  {isCurrent && (
                    <Chip
                      icon={<RocketLaunchIcon sx={{ fontSize: 13 }} />}
                      label="Building now"
                      color="primary"
                      size="small"
                      sx={{ position: 'absolute', top: 6, right: 6, height: 20, fontSize: 9.5, zIndex: 1 }}
                    />
                  )}
                  {app.live ? (
                    <CardActionArea href={app.link} target="_blank" rel="noopener noreferrer" sx={{ height: '100%' }}>
                      {cardBody}
                    </CardActionArea>
                  ) : (
                    cardBody
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </>
  );
}
